import { useLazyQuery, useMutation } from "@apollo/client";
import { Alert, Box, Grid, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import {
  EmptyContainerInput,
  GetWasteContainerQuery,
  Issue,
  IssueLevel,
  UnitType,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import {
  CONTAINER_MUTATION_REFETCH_QUERIES,
  EMPTY_CONTAINER_MUTATION,
  VALIDATE_CONTAINER_FILL_EVENT_INPUT,
} from "./api";
import {
  formatQuantity,
  isLocalDateInFuture,
  OCCURRED_AT_SHORTCUTS,
  UNIT_SELECT_ITEMS,
} from "./containerDisplay";

type WasteContainerDetail = NonNullable<GetWasteContainerQuery["wasteContainer"]>;

type EmptyContainerFormValues = {
  amount: string;
  unit: UnitType | null;
  occurredAt: string | null;
};

function localIssue(key: string, message: string): Issue {
  return {
    level: IssueLevel.Critical,
    key,
    message,
    modelName: "WasteContainer",
    dataName: null,
    jurisdictions: [],
  };
}

function toEmptyInput(
  values: EmptyContainerFormValues,
  containerId: string
): EmptyContainerInput | null {
  if (!values.occurredAt) {
    return null;
  }
  const occurredAt = toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.occurredAt));
  if (!occurredAt) {
    return null;
  }
  const amount = safeParseFloat(values.amount);
  return {
    containerId,
    // Blank amount = remove the full recorded total.
    amount: amount ?? undefined,
    unit: amount !== null ? (values.unit ?? undefined) : undefined,
    occurredAt,
  };
}

export type EmptyContainerDialogProps = {
  container: WasteContainerDetail;
  onClose: () => void;
};

/**
 * Local rules first, then the server's: a blank amount means "the whole
 * recorded total", which only makes sense if there is one, and an explicit
 * partial empty is pre-flighted as the REMOVE fill event it becomes so
 * over-remove and unit-mix warnings surface before Save.
 */
function useEmptyContainerValidation(containerId: string, hasRecordedAmount: boolean) {
  const [validateQuery] = useLazyQuery(VALIDATE_CONTAINER_FILL_EVENT_INPUT, {
    fetchPolicy: "no-cache",
  });
  return useCallback(
    async (values: EmptyContainerFormValues): Promise<Issue[]> => {
      if (!values.occurredAt) {
        return [localIssue("occurredAt", "Enter the date the tank was emptied")];
      }
      if (isLocalDateInFuture(values.occurredAt)) {
        return [localIssue("occurredAt", "The date can't be in the future")];
      }
      const amount = safeParseFloat(values.amount);
      if (amount === null) {
        // Full empty: there must be a recorded total to remove.
        return hasRecordedAmount
          ? []
          : [
              localIssue(
                "amount",
                "This container has no recorded amount to empty — enter the amount removed"
              ),
            ];
      }
      if (amount <= 0) {
        return [localIssue("amount", "The amount removed must be greater than zero")];
      }
      if (!values.unit) {
        return [localIssue("unit", "Select a unit")];
      }
      // An explicit partial empty is a REMOVE fill event; pre-flight it as
      // one so over-remove and unit-mix warnings surface here too.
      const occurredAt = toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.occurredAt));
      if (!occurredAt) {
        return [localIssue("occurredAt", "Enter the date the tank was emptied")];
      }
      const { data } = await validateQuery({
        variables: {
          input: {
            containerId,
            amount: -amount,
            unit: values.unit,
            occurredAt,
          },
        },
      });
      return data?.containerFillEventInputValidator ?? [];
    },
    [validateQuery, containerId, hasRecordedAmount]
  );
}

export function EmptyContainerDialog({ container, onClose }: EmptyContainerDialogProps) {
  const alerts = useAlerts();
  const hasRecordedAmount = container.amount != null && container.amount > 0;

  const defaultValues = useMemo<EmptyContainerFormValues>(
    () => ({
      amount: "",
      unit: container.amountUnit ?? container.capacityUnit ?? null,
      occurredAt: DateTime.local().toISODate(),
    }),
    [container]
  );

  const validateInput = useEmptyContainerValidation(container.id, hasRecordedAmount);

  const form = useValidatingForm<EmptyContainerFormValues>(
    defaultValues,
    // The container's standing issues from the query that already loaded it,
    // so they show on open rather than only after the first change.
    container.issues,
    validateInput,
    undefined,
    {
      // A background refetch mid-edit re-issues the defaults; keep what the
      // user has typed rather than resetting under their cursor.
      keepDirtyValuesOnReset: true,
    }
  );
  const { getValues, issues } = form;

  const [emptyContainer, { loading: isSaving }] = useMutation(EMPTY_CONTAINER_MUTATION, {
    refetchQueries: CONTAINER_MUTATION_REFETCH_QUERIES,
  });

  const onSubmit = async () => {
    const values = getValues();
    const input = toEmptyInput(values, container.id);
    if (!input) {
      alerts.error("Enter the date the tank was emptied");
      return;
    }
    const submitIssues = await validateInput(values);
    if (hasCriticalIssues(submitIssues)) {
      const firstCritical = submitIssues.find((issue) => issue.level === IssueLevel.Critical);
      alerts.error(firstCritical?.message ?? "Fix the critical issues before saving");
      return;
    }
    await emptyContainer({
      variables: { input },
      onCompleted: () => {
        alerts.success("Tank emptied");
        onClose();
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while emptying the tank");
      },
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      title="Empty tank"
      primaryActionButtonProps={{
        loading: isSaving,
        onClick: onSubmit,
        // No dirty gate: a full empty is valid with nothing but the
        // defaulted date — blank amount means "the full recorded total".
        disabled: hasCriticalIssues(issues),
      }}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
    >
      <EmptyContainerFields form={form} container={container} />
    </Dialog>
  );
}

/**
 * How much came out of the tank and when — plus what that does to its clock.
 * Split from the dialog shell, which owns the mutation and the save gate.
 */
function EmptyContainerFields({
  form,
  container,
}: {
  form: ReturnType<typeof useValidatingForm<EmptyContainerFormValues>>;
  container: WasteContainerDetail;
}) {
  return (
    <FormProvider {...form}>
      <Box component="form" sx={{ pt: 1 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="text.secondary">
              Current recorded total: {formatQuantity(container.amount, container.amountUnit)}.
              Leave the amount blank to remove the full recorded total. The tank stays active and
              can refill.
            </Typography>
          </Grid>
          <Grid size={{ xs: 7 }}>
            <FormTextField
              name="amount"
              label="Amount removed"
              control={form.control}
              type="number"
              helperText="Blank = the full recorded total"
            />
          </Grid>
          <Grid size={{ xs: 5 }}>
            <FormSelect
              name="unit"
              label="Unit"
              control={form.control}
              selectItems={UNIT_SELECT_ITEMS}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <DateField
              name="occurredAt"
              label="Date emptied"
              control={form.control}
              required
              shortcuts={OCCURRED_AT_SHORTCUTS}
              datePickerProps={{ disableFuture: true }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Alert severity="info" variant="outlined">
              {container.isFlowThrough
                ? "This is a flow-through tank: its accumulation clock restarts on the date it's emptied."
                : "The accumulation clock starts again with the next waste added."}
            </Alert>
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  );
}
